import React from 'react';
import { BrandedButton } from './BrandedButton';
import { KratosFlow, KratosNode } from '../services/kratosService';
import './SocialSignIn.css';

/** The flow's social sign-in buttons: oidc nodes named `provider`, valued with the provider id. */
export function socialProviders(flow: KratosFlow | null, allowed: string[] | null = null): KratosNode[] {
  return (flow?.ui?.nodes ?? []).filter(
    (node) =>
      node.group === 'oidc' &&
      node.attributes?.name === 'provider' &&
      typeof node.attributes.value === 'string' &&
      (!allowed || allowed.includes(node.attributes.value))
  );
}

/** A provider's display name ("Google"), from Kratos's label for its button. */
export function providerName(flow: KratosFlow | null, providerId: string): string {
  const node = socialProviders(flow).find((candidate) => candidate.attributes.value === providerId);
  return node?.meta?.label?.context?.provider || providerId;
}

interface SocialSignInProps {
  flow: KratosFlow | null;
  /** The application's allow-list (client metadata); null = every provider. */
  allowed?: string[] | null;
  onSelect: (providerId: string) => void;
  busy: boolean;
  /** The "or continue with" divider — off when these are the only way forward. */
  divider?: boolean;
}

/**
 * Social sign-in buttons, rendered from the Kratos flow's `oidc` nodes: one
 * per provider the operator configured (and the application allows), labelled
 * the way Kratos labels them ("Sign in with Google"). Nothing at all when
 * there are none, so the page looks as it always did until a provider is on.
 */
export const SocialSignIn: React.FC<SocialSignInProps> = ({
  flow,
  allowed = null,
  onSelect,
  busy,
  divider = true,
}) => {
  const providers = socialProviders(flow, allowed);
  if (providers.length === 0) return null;

  return (
    <div className="social-sign-in">
      {divider && (
        <div className="social-sign-in-divider" role="separator">
          <span>or continue with</span>
        </div>
      )}
      {providers.map((node) => {
        const providerId = node.attributes.value as string;
        return (
          <BrandedButton
            key={providerId}
            type="button"
            variant="outline"
            fullWidth
            disabled={busy || node.attributes.disabled}
            onClick={() => onSelect(providerId)}
          >
            {node.meta?.label?.text ?? providerId}
          </BrandedButton>
        );
      })}
    </div>
  );
};
